// Historical Backtest — Rogers 2026-08-07: lives inside MCO Detail (tab 4), no longer a
// standalone Scenario-page section. Bars: what MCO/ECO would have paid by year; selecting
// a year reveals expected → actual movement for revenue, input costs, and margin, plus the
// payment-factor detail. County-level costs are never farmer-scaled.

import { Fragment, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Card,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { backtestYearDeltas } from "api-toolkit/mco/mcoEcoBacktest";
import { BACKTEST_VINTAGES } from "api-toolkit/mco/useMco";
import { formatPrice } from "utility/formatters";

import McoBacktestChart, { COLOR_ECO, COLOR_MCO } from "./McoBacktestChart";

// Copy rule (Rogers spec): a null resolution value is "not yet determined" — never $0.
const moneyOrPending = (v, places = 2) => (v == null ? "not yet determined" : formatPrice(v, places));

function keyStats(years) {
  const resolved = years.filter((y) => y.ready);
  const mcoCumulative = resolved.reduce((sum, y) => sum + (y.indemnityPerAcre || 0), 0);
  const triggered = resolved.filter((y) => y.indemnityPerAcre > 0).length;
  const ecoResolved = resolved.filter((y) => y.eco?.ready);
  const ecoCumulative = ecoResolved.reduce((sum, y) => sum + (y.eco.result.indemnityPerAcre || 0), 0);
  return {
    triggered,
    resolvedCount: resolved.length,
    avgPaymentPerAcre: resolved.length ? mcoCumulative / resolved.length : null,
    mcoCumulative: resolved.length ? mcoCumulative : null,
    ecoCumulative: ecoResolved.length ? ecoCumulative : null,
    ecoResolvedCount: ecoResolved.length,
  };
}

function KeyStatCard({ label, value, caption }) {
  return (
    <Card variant="outlined" sx={{ px: 2, py: 1.5, minWidth: 150, flex: "1 1 150px" }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" sx={{ lineHeight: 1.3, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Card>
  );
}

const DASH = "—";
const MUTED = { color: "text.secondary" };

/**
 * Year-by-year outcomes: the margin that landed against the trigger it had to beat, what each
 * product would have paid, and which came out ahead.
 */
function YearOutcomesTable({ years, coverageLabel }) {
  const margin = (v) => (v == null ? DASH : formatPrice(v, 0));
  // A resolved year that simply didn't pay prints $0.00; only an unresolved one prints a dash.
  // Collapsing both to a dash would hide that MCO was tested that year and the margin held.
  const paid = (v) => (v == null ? DASH : formatPrice(v, 2));
  const paidSx = (v, color) => (v ? { color, fontWeight: 600 } : MUTED);

  return (
    <Table size="small" className="reportTable" sx={{ maxWidth: 780, mb: 2 }}>
      <TableHead>
        <TableRow>
          <TableCell>Year</TableCell>
          <TableCell align="right">Harvest margin</TableCell>
          <TableCell align="right">Trigger</TableCell>
          <TableCell align="right">{`MCO ${coverageLabel}`}</TableCell>
          <TableCell align="right">{`ECO ${coverageLabel}`}</TableCell>
          <TableCell align="right">MCO edge</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {years.map((y) => {
          const mco = y.ready ? y.indemnityPerAcre : null;
          const eco = y.eco?.ready ? y.eco.result.indemnityPerAcre : null;
          // The edge needs BOTH legs resolved. Treating a missing ECO as zero would report an
          // MCO win in years the comparison simply hasn't been computed for.
          const edge = mco != null && eco != null ? mco - eco : null;
          return (
            <TableRow key={y.year}>
              <TableCell sx={{ fontWeight: 600 }}>{y.year}</TableCell>
              <TableCell align="right">{margin(y.harvestMargin)}</TableCell>
              <TableCell align="right" sx={MUTED}>
                {margin(y.triggerMargin)}
              </TableCell>
              <TableCell align="right" sx={paidSx(mco, COLOR_MCO)}>
                {paid(mco)}
              </TableCell>
              <TableCell align="right" sx={paidSx(eco, COLOR_ECO)}>
                {paid(eco)}
              </TableCell>
              <TableCell align="right" sx={edge ? { color: edge > 0 ? COLOR_MCO : COLOR_ECO, fontWeight: 600 } : MUTED}>
                {edge == null ? DASH : `${edge > 0 ? "+" : edge < 0 ? "−" : ""}${formatPrice(Math.abs(edge), 0)}`}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// Which way is the good way depends on the line: revenue and margin rising is good news, input
// costs rising is not. A flat line and an unresolved one are never colored as bad.
const movedTheGoodWay = (delta, riseIsGood) => delta == null || delta === 0 || delta > 0 === riseIsGood;

/**
 * One line's expected -> actual move, as a bar growing left or right off a centre axis.
 *
 * `scale` is the largest move on the card, shared by all three bars: revenue, costs and margin
 * are the same county-level $/ac unit and the margin move IS the other two netted, so scaling
 * each line to its own maximum would draw three bars that contradict that arithmetic.
 */
function MoveBar({ delta, scale, riseIsGood }) {
  if (delta == null) {
    return (
      <Typography variant="caption" sx={MUTED}>
        not yet determined
      </Typography>
    );
  }
  const width = `${Math.min(50, (Math.abs(delta) / scale) * 50)}%`;
  const good = movedTheGoodWay(delta, riseIsGood);
  return (
    <Box sx={{ position: "relative", height: 14 }}>
      <Box sx={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: "1px", bgcolor: "divider" }} />
      <Box
        sx={{
          position: "absolute",
          top: 1,
          height: 12,
          width,
          borderRadius: 0.5,
          bgcolor: good ? "success.main" : "error.main",
          ...(delta < 0 ? { right: "50%" } : { left: "50%" }),
        }}
      />
    </Box>
  );
}

/**
 * The selected year, read as what moved between the September guarantee and the settled season:
 * revenue in, input costs out, and the margin those two leave behind — which is the number MCO
 * actually insures. County-level $/ac throughout, so these are NOT this operation's dollars;
 * the payment figures above already carry the farmer's own protection.
 */
function YearDetailCard({ year }) {
  const lines = backtestYearDeltas(year);
  const scale = Math.max(...lines.map((line) => Math.abs(line.delta ?? 0)), 1);

  return (
    <Card variant="outlined" sx={{ p: 2, mb: 2, maxWidth: 780 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {year.year}
        </Typography>
        <Typography variant="caption" sx={MUTED}>
          expected to actual, county $/acre
        </Typography>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(80px, 1fr) 90px 90px minmax(110px, 1.6fr) 120px",
          columnGap: 1.5,
          rowGap: 1,
          alignItems: "center",
        }}
      >
        {["", "Expected", "Actual", "", "Move"].map((heading, i) => (
          <Typography
            key={heading || `spacer-${i}`}
            variant="caption"
            sx={{ ...MUTED, textAlign: i ? "right" : "left" }}
          >
            {heading}
          </Typography>
        ))}

        {lines.map((line) => {
          const good = movedTheGoodWay(line.delta, line.riseIsGood);
          return (
            <Fragment key={line.id}>
              <Typography variant="body2">{line.label}</Typography>
              <Typography variant="body2" sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {line.expected == null ? DASH : formatPrice(line.expected, 2)}
              </Typography>
              <Typography variant="body2" sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {line.actual == null ? DASH : formatPrice(line.actual, 2)}
              </Typography>
              <MoveBar delta={line.delta} scale={scale} riseIsGood={line.riseIsGood} />
              <Typography
                variant="body2"
                sx={{
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 600,
                  color: line.delta == null ? "text.secondary" : good ? "success.main" : "error.main",
                }}
              >
                {line.delta == null
                  ? DASH
                  : `${line.delta > 0 ? "+" : line.delta < 0 ? "\u2212" : ""}${formatPrice(Math.abs(line.delta), 2)}`}
                {line.pct != null && (
                  <Typography component="span" variant="caption" sx={{ ...MUTED, ml: 0.75 }}>
                    {`${line.pct > 0 ? "+" : line.pct < 0 ? "\u2212" : ""}${Math.abs(line.pct)}%`}
                  </Typography>
                )}
              </Typography>
            </Fragment>
          );
        })}
      </Box>
    </Card>
  );
}

export default function McoBacktestPanel({ mco }) {
  const [selectedYear, setSelectedYear] = useState(null);

  const years = mco.backtest?.years || [];
  const stats = useMemo(() => keyStats(years), [years]);
  const activeYear = useMemo(
    () =>
      years.find((y) => y.year === selectedYear) ||
      [...years].reverse().find((y) => y.ready) ||
      years[years.length - 1],
    [years, selectedYear]
  );

  // ponytail: both payment columns are labeled at MCO's coverage level, on the assumption that
  // eco_backtest_comparison serves the comparable ECO offer at that same level. Upgrade path is
  // an explicit coverage column on that row, surfaced through eco.result.
  const coverageLabel = `${Math.round((mco.coverageStart ?? 0.95) * 100)}%`;

  return (
    <Box>
      {/* Right-aligned: the rule set is a switch you set once, not the first thing to read. */}
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Select size="small" value={mco.backtestVintage} onChange={(e) => mco.setBacktestVintage(e.target.value)}>
          {BACKTEST_VINTAGES.map((v) => (
            <MenuItem key={v.code} value={v.code}>
              {v.label}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      {!mco.backtest?.ready && (
        <Alert severity="info" sx={{ mb: 2, maxWidth: "700px" }}>
          {mco.backtest ? mco.backtest.blockers?.join("; ") || "Could not load backtest" : "Loading backtest…"}
        </Alert>
      )}

      {Boolean(years.length) && (
        <>
          <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
            <KeyStatCard label="Years triggered" value={`${stats.triggered} / ${stats.resolvedCount}`} caption="MCO" />
            <KeyStatCard
              label="Average MCO payment"
              value={moneyOrPending(stats.avgPaymentPerAcre)}
              caption="per acre"
            />
            <KeyStatCard label="MCO cumulative" value={moneyOrPending(stats.mcoCumulative)} caption="per acre" />
            <KeyStatCard
              label="ECO cumulative"
              value={moneyOrPending(stats.ecoCumulative)}
              caption={stats.ecoResolvedCount ? `per acre, ${stats.ecoResolvedCount} years` : "no ECO years"}
            />
          </Stack>

          <McoBacktestChart years={years} selectedYear={activeYear?.year ?? null} onSelectYear={setSelectedYear} />

          {activeYear && <YearDetailCard year={activeYear} />}

          <YearOutcomesTable years={years} coverageLabel={coverageLabel} />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", maxWidth: "700px", mt: 1.5, mb: 2 }}
          >
            Historical projections only: MCO was not available before 2026.
          </Typography>
        </>
      )}
    </Box>
  );
}
